/**
 * Claims Settings — Carrier-Config, E-Mail-Postfach, Schwellenwerte.
 * @page /claims/settings
 * @roles admin, manager
 * @i18n claims.settings
 * @endpoints GET/PUT /api/claims/settings, GET/POST/PUT/DELETE /api/claims/carriers,
 *   POST /api/claims/settings/test-smtp, POST /api/claims/settings/test-imap
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import useTabParam from '../../hooks/useTabParam';
import { useLanguage } from '../../context/LanguageContext';
import { PageHeader, Button, ToggleSwitch } from '../../components/ui';
import {
  Settings, ArrowLeft, Plus, Edit3, Trash2, X, Save,
  Truck, Mail, Sliders, Check, AlertTriangle, Eye, EyeOff,
  ExternalLink, Phone, User, Clock, Shield, FileText, Tag,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const isFormData = opts.body instanceof FormData;
  const headers = hasBody && !isFormData ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts,
    credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody && !isFormData ? JSON.stringify(opts.body) : opts.body,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || data.message || `HTTP ${r.status}`);
    return data;
  });
}

// ── Tabs ──

const TABS = [
  { key: 'carriers', Icon: Truck },
  { key: 'email', Icon: Mail },
  { key: 'templates', Icon: FileText }, // #1221 Bug 3
  { key: 'general', Icon: Sliders },
];

// ── Markdown renderers (minimal) ──

const mdComponents = {
  h1: ({ children }) => <h1 className="text-base font-bold mt-4 mb-2 text-[var(--text-primary)]">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold mt-4 mb-2 text-[var(--text-primary)]">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-medium mt-3 mb-1 text-[var(--text-primary)]">{children}</h3>,
  p: ({ children }) => <p className="my-1.5 text-[13px] text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2 text-[13px] text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2 text-[13px] text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">{children}</ol>,
  li: ({ children }) => <li className="text-[var(--text-primary,#1A1A2E)] dark:text-[var(--text-tertiary,#6B7280)]">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[var(--text-primary)]">{children}</strong>,
  code: ({ children }) => <code className="bg-gray-100 dark:bg-gray-800 text-emerald-600 dark:text-emerald-400 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
};

export default function ClaimsSettings() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [tab, setTab] = useTabParam('carriers');
  const [carriers, setCarriers] = useState([]);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);

  // Carrier editor
  const [editCarrier, setEditCarrier] = useState(null); // null = closed, {} = new, {id:...} = edit
  const [carrierSaving, setCarrierSaving] = useState(false);
  const [playbookTab, setPlaybookTab] = useState('damage');
  const [playbookPreview, setPlaybookPreview] = useState(false);

  // Email test
  const [testing, setTesting] = useState(null); // 'smtp' | 'imap'
  const [testResult, setTestResult] = useState(null);

  // Settings save
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [c, s] = await Promise.all([
        api('/api/claims/carriers?all=true'),
        api('/api/claims/settings'),
      ]);
      setCarriers(c);
      setConfig(s);
    } catch (err) {
      console.error('ClaimsSettings load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Config field update (local state) ──
  const updateConfig = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));

  // ── Save config ──
  const saveConfig = async () => {
    setSaving(true);
    try {
      await api('/api/claims/settings', { method: 'PUT', body: config });
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Test connection ──
  const testConnection = async (type) => {
    setTesting(type);
    setTestResult(null);
    try {
      // Save config first so test uses latest values
      await api('/api/claims/settings', { method: 'PUT', body: config });
      const data = await api(`/api/claims/settings/test-${type}`, { method: 'POST' });
      setTestResult({ type, success: true, message: data.message || t('claims.settings.connectionSuccess') });
    } catch (err) {
      setTestResult({ type, success: false, message: err.message });
    } finally {
      setTesting(null);
      setTimeout(() => setTestResult(prev => prev?.type === type ? null : prev), 6000);
    }
  };

  // ── Carrier CRUD ──
  const openNewCarrier = () => {
    setEditCarrier({
      name: '', submission_method: 'email', submission_email: '',
      submission_deadline_days: 7, processing_time_days: 15, objection_deadline_days: 14,
      followup_after_days: 10, max_liability: '', insurance_required_above: '',
      portal_url: '', contact_person: '', contact_phone: '',
      playbook_damage: '', playbook_investigation: '', is_active: true,
    });
    setPlaybookTab('damage');
    setPlaybookPreview(false);
  };

  const openEditCarrier = (c) => {
    setEditCarrier({ ...c });
    setPlaybookTab('damage');
    setPlaybookPreview(false);
  };

  const saveCarrier = async () => {
    if (!editCarrier.name) return;
    setCarrierSaving(true);
    try {
      const payload = { ...editCarrier };
      // Clean numeric fields
      for (const k of ['submission_deadline_days', 'processing_time_days', 'objection_deadline_days', 'followup_after_days']) {
        payload[k] = parseInt(payload[k]) || null;
      }
      for (const k of ['max_liability', 'insurance_required_above']) {
        payload[k] = payload[k] ? parseFloat(payload[k]) : null;
      }

      if (editCarrier.id) {
        await api(`/api/claims/carriers/${editCarrier.id}`, { method: 'PUT', body: payload });
      } else {
        await api('/api/claims/carriers', { method: 'POST', body: payload });
      }
      setEditCarrier(null);
      await loadAll();
    } catch (err) {
      alert(err.message);
    } finally {
      setCarrierSaving(false);
    }
  };

  const deleteCarrier = async (id) => {
    if (!confirm(t('claims.settings.deleteCarrier') + '?')) return;
    try {
      await api(`/api/claims/carriers/${id}`, { method: 'DELETE' });
      await loadAll();
    } catch (err) {
      alert(err.message);
    }
  };

  const updateCarrierField = (key, value) => setEditCarrier(prev => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={t('claims.settings.title')} icon={Settings}>
        <Button variant="secondary" size="sm" icon={ArrowLeft} onClick={() => navigate('/claims')}>
          {t('claims.title')}
        </Button>
      </PageHeader>

      {/* Tab bar */}
      <div className="flex items-center gap-1 bg-surface-card border border-line-card rounded-card p-1">
        {TABS.map(({ key, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 text-[12px] font-medium rounded-lg transition-colors ${
              tab === key
                ? 'bg-white dark:bg-[#2A2D3D] text-content shadow-sm'
                : 'text-content-secondary hover:text-content hover:bg-surface-hover'
            }`}
          >
            <Icon size={14} />
            {t(`claims.settings.${key}`)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'carriers' && <CarriersTab
        carriers={carriers} t={t} editCarrier={editCarrier}
        openNewCarrier={openNewCarrier} openEditCarrier={openEditCarrier}
        deleteCarrier={deleteCarrier} saveCarrier={saveCarrier}
        carrierSaving={carrierSaving} updateCarrierField={updateCarrierField}
        setEditCarrier={setEditCarrier}
        playbookTab={playbookTab} setPlaybookTab={setPlaybookTab}
        playbookPreview={playbookPreview} setPlaybookPreview={setPlaybookPreview}
      />}

      {tab === 'email' && <EmailTab
        config={config} updateConfig={updateConfig} saveConfig={saveConfig}
        saving={saving} testing={testing} testConnection={testConnection}
        testResult={testResult} t={t}
      />}

      {tab === 'templates' && <TemplatesTab t={t} />}

      {tab === 'general' && <GeneralTab
        config={config} updateConfig={updateConfig} saveConfig={saveConfig}
        saving={saving} t={t}
      />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ── TAB 1: CARRIERS ────────────────────────────────────────
// ════════════════════════════════════════════════════════════

function CarriersTab({
  carriers, t, editCarrier, openNewCarrier, openEditCarrier, deleteCarrier,
  saveCarrier, carrierSaving, updateCarrierField, setEditCarrier,
  playbookTab, setPlaybookTab, playbookPreview, setPlaybookPreview,
}) {
  return (
    <>
      {/* Carrier table */}
      <div className="bg-surface-card border border-line-card rounded-card">
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <h3 className="text-[13px] font-bold text-content">{t('claims.settings.carriers')}</h3>
          <Button size="sm" icon={Plus} onClick={openNewCarrier}>{t('claims.settings.addCarrier')}</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-line">
                {['carrierName', 'submissionMethod', 'submissionEmail', 'submissionDeadline', 'playbookDamage', 'active', ''].map((col, i) => (
                  <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold text-content-tertiary uppercase tracking-wider">
                    {col ? t(`claims.settings.${col}`) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {carriers.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[13px] text-content-secondary">{t('claims.knowledge.noArticles')}</td></tr>
              ) : carriers.map(c => (
                <tr key={c.id} className="border-b border-line last:border-0 hover:bg-surface-hover transition-colors">
                  <td className="px-4 py-3 text-[13px] font-medium text-content">{c.name}</td>
                  <td className="px-4 py-3 text-[12px] text-content-secondary capitalize">{c.submission_method || 'email'}</td>
                  <td className="px-4 py-3 text-[12px] text-content-secondary font-mono">{c.submission_email || '–'}</td>
                  <td className="px-4 py-3 text-[12px] text-content-secondary">{c.submission_deadline_days || '–'} {t('claims.settings.days') || 'Tage'}</td>
                  <td className="px-4 py-3 text-[12px]">
                    {c.playbook_damage ? (
                      <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Check size={12} /> {t('common.yes') || 'Ja'}</span>
                    ) : (
                      <span className="text-content-tertiary">–</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block w-2 h-2 rounded-full ${c.is_active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEditCarrier(c)} className="p-1.5 rounded-lg hover:bg-surface-hover text-content-secondary hover:text-content transition-colors">
                        <Edit3 size={14} />
                      </button>
                      <button onClick={() => deleteCarrier(c.id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-content-secondary hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Carrier Editor Slide-Over */}
      {editCarrier && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setEditCarrier(null)} />
          <div className="relative w-full max-w-2xl bg-surface-card shadow-xl overflow-y-auto">
            <div className="sticky top-0 z-10 bg-surface-card px-6 py-4 border-b border-line flex items-center justify-between">
              <h3 className="text-[14px] font-bold text-content">
                {editCarrier.id ? t('claims.settings.editCarrier') : t('claims.settings.addCarrier')}
              </h3>
              <button onClick={() => setEditCarrier(null)} className="p-1 rounded-lg hover:bg-surface-hover text-content-secondary">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Structured fields */}
              <div className="space-y-4">
                <h4 className="text-[12px] font-semibold text-content-tertiary uppercase tracking-wider">{t('claims.settings.basicInfo') || 'Grunddaten'}</h4>

                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('claims.settings.carrierName')} required>
                    <input value={editCarrier.name} onChange={e => updateCarrierField('name', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </Field>
                  <Field label={t('claims.settings.submissionMethod')}>
                    <select value={editCarrier.submission_method || 'email'} onChange={e => updateCarrierField('submission_method', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500">
                      <option value="email">E-Mail</option>
                      <option value="portal">Portal</option>
                      <option value="api">API</option>
                      <option value="phone">{t('claims.settings.contactPhone')}</option>
                    </select>
                  </Field>
                </div>

                <Field label={t('claims.settings.submissionEmail')}>
                  <input value={editCarrier.submission_email || ''} onChange={e => updateCarrierField('submission_email', e.target.value)}
                    className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="schaden@carrier.de" />
                </Field>

                <h4 className="text-[12px] font-semibold text-content-tertiary uppercase tracking-wider pt-2">{t('claims.settings.deadlines') || 'Fristen'}</h4>

                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('claims.settings.submissionDeadline')}>
                    <input type="number" value={editCarrier.submission_deadline_days ?? ''} onChange={e => updateCarrierField('submission_deadline_days', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </Field>
                  <Field label={t('claims.settings.processingTime')}>
                    <input type="number" value={editCarrier.processing_time_days ?? ''} onChange={e => updateCarrierField('processing_time_days', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </Field>
                  <Field label={t('claims.settings.objectionDeadline')}>
                    <input type="number" value={editCarrier.objection_deadline_days ?? ''} onChange={e => updateCarrierField('objection_deadline_days', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </Field>
                  <Field label={t('claims.settings.followupAfter')}>
                    <input type="number" value={editCarrier.followup_after_days ?? ''} onChange={e => updateCarrierField('followup_after_days', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </Field>
                </div>

                <h4 className="text-[12px] font-semibold text-content-tertiary uppercase tracking-wider pt-2">{t('claims.settings.liability') || 'Haftung'}</h4>

                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('claims.settings.maxLiability')}>
                    <input type="number" step="0.01" value={editCarrier.max_liability ?? ''} onChange={e => updateCarrierField('max_liability', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="510.00" />
                  </Field>
                  <Field label={t('claims.settings.insuranceAbove')}>
                    <input type="number" step="0.01" value={editCarrier.insurance_required_above ?? ''} onChange={e => updateCarrierField('insurance_required_above', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="500.00" />
                  </Field>
                </div>

                <h4 className="text-[12px] font-semibold text-content-tertiary uppercase tracking-wider pt-2">{t('claims.settings.contact') || 'Kontakt'}</h4>

                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('claims.settings.portalUrl')}>
                    <input value={editCarrier.portal_url || ''} onChange={e => updateCarrierField('portal_url', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="https://portal.carrier.com" />
                  </Field>
                  <Field label={t('claims.settings.contactPerson')}>
                    <input value={editCarrier.contact_person || ''} onChange={e => updateCarrierField('contact_person', e.target.value)}
                      className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  </Field>
                </div>

                <Field label={t('claims.settings.contactPhone')}>
                  <input value={editCarrier.contact_phone || ''} onChange={e => updateCarrierField('contact_phone', e.target.value)}
                    className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                    placeholder="+43 1 234 5678" />
                </Field>

                {/* #1217 A2: Service-Regionen (Lager-Filter) */}
                <Field label={t('claims.settings.serviceRegions')}>
                  <p className="text-[11px] text-content-tertiary mb-2">{t('claims.settings.serviceRegionsHint')}</p>
                  <div className="flex gap-4">
                    {['AT', 'DE'].map(region => {
                      const arr = Array.isArray(editCarrier.service_regions) ? editCarrier.service_regions : [];
                      const checked = arr.includes(region);
                      return (
                        <label key={region} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => {
                              const next = e.target.checked
                                ? [...arr, region]
                                : arr.filter(r => r !== region);
                              updateCarrierField('service_regions', next);
                            }}
                            className="rounded text-[#338edc] focus:ring-[#338edc]"
                          />
                          <span className="text-[13px] text-content">
                            {region === 'AT' ? t('claims.form.lagerAT') : t('claims.form.lagerDE')}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </Field>

                {/* Active toggle */}
                <label className="flex items-center gap-2 cursor-pointer pt-1">
                  <input type="checkbox" checked={editCarrier.is_active !== false}
                    onChange={e => updateCarrierField('is_active', e.target.checked)}
                    className="rounded text-[#338edc] focus:ring-[#338edc]" />
                  <span className="text-[13px] text-content">{t('claims.settings.active') || 'Aktiv'}</span>
                </label>
              </div>

              {/* AI Playbooks */}
              <div>
                <h4 className="text-[12px] font-semibold text-content-tertiary uppercase tracking-wider mb-3">AI Playbooks</h4>

                {/* Playbook tabs */}
                <div className="flex items-center gap-1 mb-3 bg-surface-inset rounded-button p-1">
                  <button
                    onClick={() => { setPlaybookTab('damage'); setPlaybookPreview(false); }}
                    className={`px-3 py-1.5 text-[12px] font-medium rounded-lg transition-colors ${
                      playbookTab === 'damage' ? 'bg-white dark:bg-[#2A2D3D] text-content shadow-sm' : 'text-content-secondary hover:text-content'
                    }`}
                  >
                    {t('claims.settings.playbookDamage')}
                  </button>
                  <button
                    onClick={() => { setPlaybookTab('investigation'); setPlaybookPreview(false); }}
                    className={`px-3 py-1.5 text-[12px] font-medium rounded-lg transition-colors ${
                      playbookTab === 'investigation' ? 'bg-white dark:bg-[#2A2D3D] text-content shadow-sm' : 'text-content-secondary hover:text-content'
                    }`}
                  >
                    {t('claims.settings.playbookInvestigation')}
                  </button>
                  <button
                    onClick={() => setPlaybookPreview(!playbookPreview)}
                    className="ml-auto flex items-center gap-1 px-2 py-1 text-[11px] text-[#338edc] hover:underline"
                  >
                    {playbookPreview ? <Edit3 size={11} /> : <Eye size={11} />}
                    {playbookPreview ? 'Editor' : t('claims.settings.preview')}
                  </button>
                </div>

                <p className="text-[11px] text-content-tertiary mb-2">{t('claims.settings.playbookHint')}</p>

                {playbookPreview ? (
                  <div className="min-h-[300px] p-4 bg-surface-card border border-line rounded-button prose prose-sm max-w-none dark:prose-invert">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {editCarrier[playbookTab === 'damage' ? 'playbook_damage' : 'playbook_investigation'] || ''}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <textarea
                    value={editCarrier[playbookTab === 'damage' ? 'playbook_damage' : 'playbook_investigation'] || ''}
                    onChange={e => updateCarrierField(playbookTab === 'damage' ? 'playbook_damage' : 'playbook_investigation', e.target.value)}
                    rows={14}
                    className="w-full px-3 py-2 text-[13px] font-mono bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y min-h-[300px]"
                    placeholder={`Schritt 1: ...\nSchritt 2: ...\nSchritt 3: ...`}
                  />
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-surface-card px-6 py-4 border-t border-line flex justify-end gap-3">
              <Button variant="secondary" size="sm" onClick={() => setEditCarrier(null)}>
                {t('common.cancel') || 'Abbrechen'}
              </Button>
              <Button size="sm" icon={Save} onClick={saveCarrier} disabled={carrierSaving || !editCarrier.name}>
                {carrierSaving ? '...' : (t('common.save') || 'Speichern')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════
// ── TAB 2: EMAIL ───────────────────────────────────────────
// ════════════════════════════════════════════════════════════

function EmailTab({ config, updateConfig, saveConfig, saving, testing, testConnection, testResult, t }) {
  return (
    <div className="space-y-4">
      {/* IMAP-Worker Master-Toggle + Health (#1219 E) */}
      <EmailWorkerHealthCard config={config} updateConfig={updateConfig} saveConfig={saveConfig} saving={saving} t={t} />

      {/* Auto-Status-Rules (#1219 E) */}
      <AutoStatusRulesCard t={t} />

      {/* SMTP */}
      <div className="bg-surface-card border border-line-card rounded-card">
        <div className="px-6 py-4 border-b border-line flex items-center gap-2">
          <Mail size={14} className="text-content" />
          <h3 className="text-[13px] font-bold text-content">{t('claims.settings.smtpConfig')}</h3>
          {config.claims_smtp_host ? (
            <span className="ml-auto text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Check size={12} /> {t('claims.settings.configured') || 'Konfiguriert'}</span>
          ) : (
            <span className="ml-auto text-[11px] text-amber-500 flex items-center gap-1"><AlertTriangle size={12} /> {t('claims.settings.notConfigured') || 'Nicht konfiguriert'}</span>
          )}
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="SMTP Host">
              <input value={config.claims_smtp_host || ''} onChange={e => updateConfig('claims_smtp_host', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="mail.example.com" />
            </Field>
            <Field label="SMTP Port">
              <input type="number" value={config.claims_smtp_port || '587'} onChange={e => updateConfig('claims_smtp_port', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </Field>
            <Field label={t('claims.settings.username') || 'Benutzername'}>
              <input value={config.claims_smtp_user || ''} onChange={e => updateConfig('claims_smtp_user', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </Field>
            <Field label={t('claims.settings.password') || 'Passwort'}>
              <input type="password" value={config.claims_smtp_pass || ''} onChange={e => updateConfig('claims_smtp_pass', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label={t('claims.settings.senderAddress') || 'Absender-Adresse'}>
              <input value={config.claims_email_from || ''} onChange={e => updateConfig('claims_email_from', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="claims@weship.eu" />
            </Field>
          </div>
        </div>
        <div className="px-6 pb-4 flex items-center gap-3">
          <button
            onClick={() => testConnection('smtp')}
            disabled={testing === 'smtp'}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-colors border border-line bg-surface-card text-content-secondary hover:text-content hover:bg-surface-hover disabled:opacity-50"
          >
            {testing === 'smtp' ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                {t('claims.settings.testConnection')}
              </span>
            ) : t('claims.settings.testConnection')}
          </button>
          <button onClick={saveConfig} disabled={saving}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#338edc] hover:bg-[#2b7bc4]">
            {saving ? (t('common.saving') || '...') : (t('common.save') || 'Speichern')}
          </button>
        </div>
        {testResult?.type === 'smtp' && (
          <div className={`mx-6 mb-4 px-3 py-2 rounded-lg text-xs font-medium ${
            testResult.success
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
              : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
          }`}>
            {testResult.success ? '\u2713 ' : '\u2717 '}{testResult.message}
          </div>
        )}
      </div>

      {/* IMAP */}
      <div className="bg-surface-card border border-line-card rounded-card">
        <div className="px-6 py-4 border-b border-line flex items-center gap-2">
          <Mail size={14} className="text-content" />
          <h3 className="text-[13px] font-bold text-content">{t('claims.settings.imapConfig')}</h3>
          {config.claims_imap_host ? (
            <span className="ml-auto text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Check size={12} /> {t('claims.settings.configured') || 'Konfiguriert'}</span>
          ) : (
            <span className="ml-auto text-[11px] text-amber-500 flex items-center gap-1"><AlertTriangle size={12} /> {t('claims.settings.notConfigured') || 'Nicht konfiguriert'}</span>
          )}
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="IMAP Host">
              <input value={config.claims_imap_host || ''} onChange={e => updateConfig('claims_imap_host', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="imap.example.com" />
            </Field>
            <Field label="IMAP Port">
              <input type="number" value={config.claims_imap_port || '993'} onChange={e => updateConfig('claims_imap_port', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </Field>
            <Field label={t('claims.settings.username') || 'Benutzername'}>
              <input value={config.claims_imap_user || ''} onChange={e => updateConfig('claims_imap_user', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </Field>
            <Field label={t('claims.settings.password') || 'Passwort'}>
              <input type="password" value={config.claims_imap_pass || ''} onChange={e => updateConfig('claims_imap_pass', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </Field>
          </div>
          <div className="mt-4">
            <Field label={t('claims.settings.pollingInterval') || 'Polling-Intervall (Minuten)'}>
              <input type="number" value={config.claims_imap_poll_interval || '5'} onChange={e => updateConfig('claims_imap_poll_interval', e.target.value)}
                className="w-full px-3 py-2 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
                min="1" max="60" />
            </Field>
          </div>
        </div>
        <div className="px-6 pb-4 flex items-center gap-3">
          <button
            onClick={() => testConnection('imap')}
            disabled={testing === 'imap'}
            className="px-4 py-1.5 rounded-lg text-sm font-medium transition-colors border border-line bg-surface-card text-content-secondary hover:text-content hover:bg-surface-hover disabled:opacity-50"
          >
            {testing === 'imap' ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full" />
                {t('claims.settings.testConnection')}
              </span>
            ) : t('claims.settings.testConnection')}
          </button>
          <button onClick={saveConfig} disabled={saving}
            className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#338edc] hover:bg-[#2b7bc4]">
            {saving ? (t('common.saving') || '...') : (t('common.save') || 'Speichern')}
          </button>
        </div>
        {testResult?.type === 'imap' && (
          <div className={`mx-6 mb-4 px-3 py-2 rounded-lg text-xs font-medium ${
            testResult.success
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
              : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
          }`}>
            {testResult.success ? '\u2713 ' : '\u2717 '}{testResult.message}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ── TAB 3: GENERAL ─────────────────────────────────────────
// ════════════════════════════════════════════════════════════

function GeneralTab({ config, updateConfig, saveConfig, saving, t }) {
  return (
    <div className="bg-surface-card border border-line-card rounded-card">
      <div className="px-6 py-4 border-b border-line">
        <h3 className="text-[13px] font-bold text-content">{t('claims.settings.general')}</h3>
      </div>
      <div className="px-6 py-4 space-y-0">
        {/* Value threshold */}
        <SettingRow label={t('claims.settings.valueThreshold')}>
          <input type="number" value={config.claims_value_threshold || ''} onChange={e => updateConfig('claims_value_threshold', e.target.value)}
            className="w-full px-3 py-1.5 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="500" />
        </SettingRow>

        {/* Max per month */}
        <SettingRow label={t('claims.settings.maxClaimsPerMonth')}>
          <input type="number" value={config.claims_max_per_customer_month || ''} onChange={e => updateConfig('claims_max_per_customer_month', e.target.value)}
            className="w-full px-3 py-1.5 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="10" />
        </SettingRow>

        {/* Template URL */}
        <SettingRow label={t('claims.settings.templateUrl')}>
          <input value={config.claims_template_url || ''} onChange={e => updateConfig('claims_template_url', e.target.value)}
            className="w-full px-3 py-1.5 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="https://..." />
        </SettingRow>

        {/* Max file size */}
        <SettingRow label={t('claims.settings.maxFileSize') || 'Max. Dateigröße (MB)'}>
          <input type="number" value={config.claims_max_file_size_mb || ''} onChange={e => updateConfig('claims_max_file_size_mb', e.target.value)}
            className="w-full px-3 py-1.5 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="10" min="1" max="50" />
        </SettingRow>

        {/* Number prefix */}
        <SettingRow label={t('claims.settings.numberPrefix')}>
          <input value={config.claims_number_prefix || ''} onChange={e => updateConfig('claims_number_prefix', e.target.value)}
            className="w-full px-3 py-1.5 text-[13px] bg-surface-card border border-line rounded-button text-content focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder="CLM" />
        </SettingRow>

        {/* Auto submit toggle */}
        <SettingRow label={t('claims.settings.autoSubmit')} description={t('claims.settings.autoSubmitDesc') || 'Standard-Fälle werden automatisch beim Carrier eingereicht'}>
          <ToggleBtn checked={config.claims_auto_submit === 'true' || config.claims_auto_submit === true}
            onChange={v => updateConfig('claims_auto_submit', v ? 'true' : 'false')} />
        </SettingRow>

        {/* Auto notify toggle */}
        <SettingRow label={t('claims.settings.autoNotify')} description={t('claims.settings.autoNotifyDesc') || 'Kunden erhalten bei jedem Status-Wechsel eine E-Mail'}>
          <ToggleBtn checked={config.claims_auto_notify === 'true' || config.claims_auto_notify === true}
            onChange={v => updateConfig('claims_auto_notify', v ? 'true' : 'false')} />
        </SettingRow>
      </div>

      <div className="px-6 pb-4 flex justify-end">
        <button onClick={saveConfig} disabled={saving}
          className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#338edc] hover:bg-[#2b7bc4]">
          {saving ? (t('common.saving') || '...') : (t('common.save') || 'Speichern')}
        </button>
      </div>
    </div>
  );
}

// ── Shared small components ──

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-content-secondary mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function SettingRow({ label, description, children }) {
  return (
    <div className="flex items-start justify-between gap-6 py-4 border-b border-line last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-content">{label}</div>
        {description && <div className="text-xs text-content-secondary mt-0.5">{description}</div>}
      </div>
      <div className="flex-shrink-0 w-56">
        {children}
      </div>
    </div>
  );
}

function ToggleBtn({ checked, onChange }) {
  return <ToggleSwitch checked={checked} onChange={onChange} />;
}

// ════════════════════════════════════════════════════════════
// #1219 Block E — Email-Worker-Health + Auto-Status-Rules
// ════════════════════════════════════════════════════════════

function EmailWorkerHealthCard({ config, updateConfig, saveConfig, saving, t }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  // #1221 Bug 7b: "Jetzt abrufen"-Button-State
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/claims/settings/email-status');
      setStatus(data);
    } catch (err) {
      console.error('Email status fetch error:', err);
      setStatus({ error: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const pollNow = useCallback(async () => {
    setPolling(true);
    setPollResult(null);
    try {
      const data = await api('/api/claims/settings/email-poll-now', { method: 'POST' });
      setPollResult({
        ok: true,
        fetched: data.fetched ?? 0,
        processed: data.processed ?? 0,
        assigned: data.assigned ?? 0,
        unassigned: data.unassigned ?? 0,
        errors: data.errors ?? 0,
        skipped: data.skipped,
      });
      await fetchStatus();
    } catch (err) {
      setPollResult({ ok: false, error: err.message });
    } finally {
      setPolling(false);
    }
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 30_000);
    return () => clearInterval(iv);
  }, [fetchStatus]);

  const enabled = config.claims_imap_enabled === 'true' || config.claims_imap_enabled === true;
  const lastPoll = status?.last_poll_at ? new Date(status.last_poll_at) : null;
  const lastPollAgoMin = lastPoll ? Math.round((Date.now() - lastPoll.getTime()) / 60_000) : null;
  const isStale = lastPollAgoMin !== null && lastPollAgoMin > 10;

  return (
    <div className="bg-surface-card border border-line-card rounded-card">
      <div className="px-6 py-4 border-b border-line flex items-center gap-2">
        <Shield size={14} className="text-content" />
        <h3 className="text-[13px] font-bold text-content">{t('claims.settings.emailWorker.title') || 'Email-Worker (Inbound)'}</h3>
        {enabled ? (
          <span className="ml-auto text-[11px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
            <Check size={12} /> {t('claims.settings.emailWorker.enabled') || 'Aktiv'}
          </span>
        ) : (
          <span className="ml-auto text-[11px] text-amber-500 flex items-center gap-1">
            <AlertTriangle size={12} /> {t('claims.settings.emailWorker.disabled') || 'Deaktiviert'}
          </span>
        )}
      </div>
      <div className="px-6 py-4 space-y-3">
        <SettingRow
          label={t('claims.settings.emailWorker.toggleLabel') || 'Inbound-Poller aktiviert'}
          description={t('claims.settings.emailWorker.toggleDesc') || 'Cronjob claims_inbound_email_poll pollt eingehende Carrier-Mails alle 3 Min.'}
        >
          <ToggleBtn checked={enabled} onChange={v => updateConfig('claims_imap_enabled', v ? 'true' : 'false')} />
        </SettingRow>

        {/* Stats Grid */}
        {loading ? (
          <div className="text-[12px] text-content-tertiary py-2">{t('common.loading') || 'Lade…'}</div>
        ) : status?.error ? (
          <div className="text-[12px] text-red-500 bg-red-500/5 border border-red-500/20 rounded p-2">
            {status.error}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
            <div className="bg-surface-inset rounded p-3">
              <div className="text-[10px] text-content-tertiary uppercase font-medium">{t('claims.settings.emailWorker.lastPoll') || 'Letzter Poll'}</div>
              <div className={`text-[13px] font-semibold mt-1 ${isStale ? 'text-amber-500' : 'text-content'}`}>
                {lastPoll ? lastPoll.toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}
              </div>
              {lastPollAgoMin !== null && (
                <div className="text-[10px] text-content-tertiary mt-0.5">{t('claims.settings.emailWorker.ago', { min: lastPollAgoMin }) || `vor ${lastPollAgoMin} Min`}</div>
              )}
            </div>
            <div className="bg-surface-inset rounded p-3">
              <div className="text-[10px] text-content-tertiary uppercase font-medium">{t('claims.settings.emailWorker.assigned24h') || 'Zugeordnet (24h)'}</div>
              <div className="text-[13px] font-semibold text-content mt-1">{status?.assigned_24h ?? 0}</div>
            </div>
            <div className="bg-surface-inset rounded p-3">
              <div className="text-[10px] text-content-tertiary uppercase font-medium">{t('claims.settings.emailWorker.unassigned') || 'Unassigned'}</div>
              <div className="text-[13px] font-semibold text-content mt-1">{status?.unassigned_pending ?? 0}</div>
            </div>
            <div className="bg-surface-inset rounded p-3">
              <div className="text-[10px] text-content-tertiary uppercase font-medium">{t('claims.settings.emailWorker.errors24h') || 'Fehler (24h)'}</div>
              <div className={`text-[13px] font-semibold mt-1 ${(status?.errors_24h ?? 0) > 0 ? 'text-red-500' : 'text-content'}`}>{status?.errors_24h ?? 0}</div>
            </div>
          </div>
        )}

        {status?.last_error && (
          <div className="text-[11px] text-red-500 bg-red-500/5 border border-red-500/20 rounded p-2 font-mono">
            <div className="font-semibold mb-0.5">{t('claims.settings.emailWorker.lastError') || 'Letzter Fehler:'}</div>
            {status.last_error}
          </div>
        )}
      </div>
      {/* #1221 Bug 7b: Poll-Now-Result Banner */}
      {pollResult && (
        <div className={`mx-6 mb-3 rounded p-2 text-[11px] border ${pollResult.ok ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400' : 'border-red-500/30 bg-red-500/5 text-red-600'}`}>
          {pollResult.ok ? (
            pollResult.skipped === 'disabled'
              ? (t('claims.settings.emailWorker.pollNowDisabled') || 'IMAP-Poller ist deaktiviert — nichts abgerufen.')
              : pollResult.skipped
                ? (t('claims.settings.emailWorker.pollNowSkipped') || 'Bereits ein Poll-Vorgang aktiv.')
                : `${t('claims.settings.emailWorker.pollNowOk') || 'Poll abgeschlossen:'} fetched=${pollResult.fetched}, processed=${pollResult.processed}, assigned=${pollResult.assigned}, unassigned=${pollResult.unassigned}, errors=${pollResult.errors}`
          ) : (
            `${t('claims.settings.emailWorker.pollNowError') || 'Fehler:'} ${pollResult.error}`
          )}
        </div>
      )}
      <div className="px-6 pb-4 flex items-center gap-3">
        <button onClick={fetchStatus} className="px-3 py-1 text-[11px] rounded border border-line bg-surface-card hover:bg-surface-hover text-content-secondary">
          {t('common.refresh') || 'Aktualisieren'}
        </button>
        <button
          onClick={pollNow}
          disabled={polling || !(config.claims_imap_enabled === 'true' || config.claims_imap_enabled === true)}
          className="px-3 py-1 text-[11px] rounded text-white bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          title={t('claims.settings.emailWorker.pollNowHint') || 'IMAP-Inbox sofort abfragen'}
        >
          {polling ? (t('claims.settings.emailWorker.pollingNow') || 'Hole ab…') : (t('claims.settings.emailWorker.pollNow') || 'Jetzt abrufen')}
        </button>
        <button onClick={saveConfig} disabled={saving}
          className="ml-auto px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-[#338edc] hover:bg-[#2b7bc4]">
          {saving ? (t('common.saving') || '...') : (t('common.save') || 'Speichern')}
        </button>
      </div>
    </div>
  );
}

function AutoStatusRulesCard({ t }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/claims/settings/auto-status-rules');
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Status rules fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const toggleRule = async (rule, patch) => {
    try {
      await api(`/api/claims/settings/auto-status-rules/${rule.id}`, { method: 'PUT', body: patch });
      await fetchRules();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="bg-surface-card border border-line-card rounded-card">
      <div className="px-6 py-4 border-b border-line flex items-center gap-2">
        <Sliders size={14} className="text-content" />
        <h3 className="text-[13px] font-bold text-content">{t('claims.settings.autoStatusRules.title') || 'Auto-Status-Regeln'}</h3>
        <span className="ml-auto text-[11px] text-content-tertiary">{rules.filter(r => r.aktiv).length}/{rules.length} {t('claims.settings.autoStatusRules.active') || 'aktiv'}</span>
      </div>
      <div className="px-6 py-3">
        <p className="text-[11px] text-content-tertiary mb-3">
          {t('claims.settings.autoStatusRules.desc') || 'Eingehende Carrier-Mail verschiebt den Claim-Status automatisch (Status-Übergang).'}
        </p>
        {loading ? (
          <div className="text-[12px] text-content-tertiary py-2">{t('common.loading') || 'Lade…'}</div>
        ) : rules.length === 0 ? (
          <div className="text-[12px] text-content-tertiary py-2">{t('claims.settings.autoStatusRules.empty') || 'Keine Regeln konfiguriert'}</div>
        ) : (
          <div className="space-y-2">
            {rules.map(rule => (
              <div key={rule.id} className="flex items-center gap-3 p-2 rounded border border-line">
                <ToggleBtn checked={rule.aktiv} onChange={v => toggleRule(rule, { aktiv: v })} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium text-content flex items-center gap-2">
                    <span className="font-mono bg-surface-inset rounded px-1.5 py-0.5 text-[11px]">{rule.from_status}</span>
                    <span className="text-content-tertiary">→</span>
                    <span className="font-mono bg-surface-inset rounded px-1.5 py-0.5 text-[11px]">{rule.to_status}</span>
                  </div>
                  {rule.description && <div className="text-[10px] text-content-tertiary mt-0.5 truncate">{rule.description}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ── TAB 4: TEMPLATES (#1221 Bug 3) ───────────────────────
// ════════════════════════════════════════════════════════════

const TEMPLATE_LABELS = {
  claim_submitted: 'Eingangsbestätigung',
  claim_documents_missing: 'Dokumente fehlen',
  claim_at_carrier: 'Beim Carrier eingereicht',
  claim_carrier_response: 'Carrier-Antwort eingegangen',
  claim_resolved_refund: 'Erstattet',
  claim_resolved_rejected: 'Abgelehnt',
  claim_reminder_stillstand: 'Stillstand-Reminder',
  claim_carrier_deadline_exceeded: 'Carrier-Deadline überschritten',
  claim_carrier_reply_generic: 'Carrier-Reply (generisch)',
  claim_carrier_reply_followup: 'Carrier-Reply (Followup)',
};

function TemplatesTab({ t }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id, funktion, name, betreff, body_html, variablen, aktiv }
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // { subject, html }
  const [aiSuggesting, setAiSuggesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/email/templates');
      const list = (data.templates || []).filter(tp => tp.category === 'claims' || (tp.funktion || '').startsWith('claim'));
      list.sort((a, b) => (TEMPLATE_LABELS[a.funktion] || a.funktion).localeCompare(TEMPLATE_LABELS[b.funktion] || b.funktion));
      setTemplates(list);
    } catch (err) {
      console.error('Templates load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (tpl) => {
    setEditing({
      ...tpl,
      variablen: Array.isArray(tpl.variablen) ? tpl.variablen : (typeof tpl.variablen === 'string' ? JSON.parse(tpl.variablen || '[]') : []),
    });
    setPreview(null);
  };

  const closeEdit = () => { setEditing(null); setPreview(null); };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api(`/api/email/templates/${editing.id}`, {
        method: 'PUT',
        body: {
          name: editing.name,
          betreff: editing.betreff,
          body_html: editing.body_html,
          variablen: editing.variablen,
          aktiv: editing.aktiv,
        },
      });
      await load();
      closeEdit();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!editing) return;
    try {
      const data = await api(`/api/email/templates/${editing.id}/preview`, {
        method: 'POST',
        body: {
          subject: editing.betreff,
          body_html: editing.body_html,
        },
      });
      setPreview({ subject: data.subject || editing.betreff, html: data.html || data.preview_html || editing.body_html });
    } catch (err) {
      alert(err.message);
    }
  };

  const toggleActive = async (tpl) => {
    try {
      await api(`/api/email/templates/${tpl.id}/toggle-active`, { method: 'PATCH' });
      await load();
    } catch (err) {
      alert(err.message);
    }
  };

  const insertVariable = (varName) => {
    if (!editing) return;
    const token = `{{${varName}}}`;
    const ta = document.getElementById('claims-tpl-body');
    if (ta && typeof ta.selectionStart === 'number') {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newVal = (editing.body_html || '').slice(0, start) + token + (editing.body_html || '').slice(end);
      setEditing({ ...editing, body_html: newVal });
      setTimeout(() => { ta.focus(); ta.setSelectionRange(start + token.length, start + token.length); }, 0);
    } else {
      setEditing({ ...editing, body_html: (editing.body_html || '') + token });
    }
  };

  const aiAugment = async () => {
    if (!editing) return;
    setAiSuggesting(true);
    try {
      const data = await api('/api/claims/templates/ai-suggest', {
        method: 'POST',
        body: {
          funktion: editing.funktion,
          subject: editing.betreff,
          body_html: editing.body_html,
          variablen: editing.variablen,
        },
      });
      if (data?.subject || data?.body_html) {
        setEditing({
          ...editing,
          betreff: data.subject || editing.betreff,
          body_html: data.body_html || editing.body_html,
        });
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setAiSuggesting(false);
    }
  };

  return (
    <div className="bg-surface-card border border-line-card rounded-card">
      <div className="px-6 py-4 border-b border-line flex items-center gap-2">
        <FileText size={14} className="text-content" />
        <h3 className="text-[13px] font-bold text-content">{t('claims.settings.templates.title') || 'E-Mail-Templates'}</h3>
        <span className="ml-auto text-[11px] text-content-tertiary">{templates.length} {t('claims.settings.templates.count') || 'Templates'}</span>
      </div>
      <div className="px-6 py-3">
        <p className="text-[11px] text-content-tertiary mb-3">
          {t('claims.settings.templates.desc') || 'Status-Mails, Carrier-Replies und Reminder. {{platzhalter}} werden beim Versand mit Daten aus dem Claim ersetzt.'}
        </p>
        {loading ? (
          <div className="text-[12px] text-content-tertiary py-2">{t('common.loading') || 'Lade…'}</div>
        ) : templates.length === 0 ? (
          <div className="text-[12px] text-content-tertiary py-2">{t('claims.settings.templates.empty') || 'Keine Claims-Templates vorhanden.'}</div>
        ) : (
          <div className="space-y-2">
            {templates.map(tpl => (
              <div key={tpl.id} className="flex items-center gap-3 p-3 rounded border border-line">
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium text-content flex items-center gap-2">
                    {TEMPLATE_LABELS[tpl.funktion] || tpl.name}
                    {tpl.readonly && <span className="text-[10px] text-content-tertiary bg-surface-inset rounded px-1.5 py-0.5">readonly</span>}
                    {!tpl.aktiv && <span className="text-[10px] text-amber-600 bg-amber-500/10 rounded px-1.5 py-0.5">inaktiv</span>}
                  </div>
                  <div className="text-[10px] text-content-tertiary mt-0.5 font-mono truncate">{tpl.funktion}</div>
                  <div className="text-[11px] text-content-secondary mt-1 truncate">{tpl.betreff}</div>
                </div>
                <button
                  onClick={() => toggleActive(tpl)}
                  disabled={tpl.readonly}
                  className="px-2 py-1 text-[11px] rounded border border-line bg-surface-card hover:bg-surface-hover text-content-secondary disabled:opacity-50"
                >
                  {tpl.aktiv ? (t('common.deactivate') || 'Deaktivieren') : (t('common.activate') || 'Aktivieren')}
                </button>
                <button
                  onClick={() => startEdit(tpl)}
                  className="px-2 py-1 text-[11px] rounded bg-primary-500/10 text-primary-600 hover:bg-primary-500/20 flex items-center gap-1"
                >
                  <Edit3 className="w-3 h-3" />
                  {t('common.edit') || 'Bearbeiten'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !saving && closeEdit()}>
          <div className="bg-surface-card border border-line-card rounded-card w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-line flex items-center gap-2">
              <FileText size={14} className="text-content" />
              <h3 className="text-[14px] font-bold text-content flex-1">
                {TEMPLATE_LABELS[editing.funktion] || editing.name}
                <span className="text-[10px] text-content-tertiary ml-2 font-mono font-normal">{editing.funktion}</span>
              </h3>
              <button onClick={closeEdit} disabled={saving} className="text-content-tertiary hover:text-content">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-3">
                <div>
                  <label className="block text-[11px] font-medium text-content-secondary mb-1">{t('claims.settings.templates.name') || 'Name'}</label>
                  <input
                    type="text" value={editing.name || ''}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                    disabled={editing.readonly}
                    className="w-full px-3 py-2 text-[12px] bg-surface-card border border-line rounded-button text-content disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-content-secondary mb-1">{t('claims.settings.templates.subject') || 'Betreff'}</label>
                  <input
                    type="text" value={editing.betreff || ''}
                    onChange={e => setEditing({ ...editing, betreff: e.target.value })}
                    disabled={editing.readonly}
                    className="w-full px-3 py-2 text-[12px] bg-surface-card border border-line rounded-button text-content disabled:opacity-50 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-content-secondary mb-1">{t('claims.settings.templates.bodyHtml') || 'Body (HTML)'}</label>
                  <textarea
                    id="claims-tpl-body"
                    value={editing.body_html || ''}
                    onChange={e => setEditing({ ...editing, body_html: e.target.value })}
                    disabled={editing.readonly}
                    rows={16}
                    className="w-full px-3 py-2 text-[11px] bg-surface-card border border-line rounded-button text-content disabled:opacity-50 font-mono leading-relaxed"
                  />
                </div>
              </div>

              <div className="md:col-span-1 space-y-3">
                <div>
                  <div className="text-[11px] font-medium text-content-secondary mb-1 flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {t('claims.settings.templates.variables') || 'Variablen'}
                  </div>
                  <div className="bg-surface-inset rounded p-2 space-y-1 max-h-[280px] overflow-y-auto">
                    {(editing.variablen || []).length === 0 ? (
                      <div className="text-[10px] text-content-tertiary py-1">— {t('claims.settings.templates.noVariables') || 'Keine'} —</div>
                    ) : (
                      (editing.variablen || []).map(v => (
                        <button
                          key={v}
                          onClick={() => insertVariable(v)}
                          disabled={editing.readonly}
                          className="w-full text-left px-2 py-1 text-[11px] rounded hover:bg-surface-hover text-content font-mono flex items-center justify-between gap-1 disabled:opacity-50"
                          title={t('claims.settings.templates.insertHint') || 'In Body einfügen'}
                        >
                          <span>{`{{${v}}}`}</span>
                          <Plus className="w-3 h-3 text-content-tertiary" />
                        </button>
                      ))
                    )}
                  </div>
                  <div className="text-[10px] text-content-tertiary mt-1">
                    {t('claims.settings.templates.variablesHint') || 'Klick fügt den Platzhalter an der Cursor-Position ein.'}
                  </div>
                </div>

                <button
                  onClick={aiAugment}
                  disabled={editing.readonly || aiSuggesting}
                  className="w-full px-3 py-2 text-[11px] rounded border border-line bg-surface-card hover:bg-surface-hover text-content-secondary disabled:opacity-50 flex items-center justify-center gap-1.5"
                  title={t('claims.settings.templates.aiHint') || 'Claude verfeinert Tonalität + Struktur.'}
                >
                  <Shield className="w-3 h-3" />
                  {aiSuggesting ? (t('claims.settings.templates.aiBusy') || 'Verfeinere…') : (t('claims.settings.templates.aiSuggest') || 'AI-Verfeinern')}
                </button>

                <button
                  onClick={runPreview}
                  className="w-full px-3 py-2 text-[11px] rounded bg-primary-500/10 text-primary-600 hover:bg-primary-500/20 flex items-center justify-center gap-1.5"
                >
                  <Eye className="w-3 h-3" />
                  {t('claims.settings.templates.preview') || 'Vorschau'}
                </button>
              </div>

              {preview && (
                <div className="md:col-span-3 border border-line rounded-card overflow-hidden">
                  <div className="px-3 py-2 bg-surface-inset border-b border-line text-[11px] font-medium text-content-secondary flex items-center gap-2">
                    <Eye className="w-3 h-3" />
                    {t('claims.settings.templates.previewLabel') || 'Vorschau (Variablen werden mit Demo-Daten ersetzt)'}
                    <span className="ml-auto font-mono text-content-tertiary">{preview.subject}</span>
                  </div>
                  <iframe
                    srcDoc={preview.html}
                    title="preview"
                    sandbox=""
                    className="w-full h-[300px] bg-white"
                  />
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-line flex items-center gap-2">
              <label className="flex items-center gap-2 text-[11px] text-content-secondary">
                <input
                  type="checkbox" checked={!!editing.aktiv}
                  onChange={e => setEditing({ ...editing, aktiv: e.target.checked })}
                  disabled={editing.readonly}
                />
                {t('claims.settings.templates.active') || 'Aktiv'}
              </label>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={closeEdit} disabled={saving}>
                  {t('common.cancel') || 'Abbrechen'}
                </Button>
                <Button
                  variant="primary" size="sm" onClick={save}
                  disabled={saving || editing.readonly} icon={Save}
                >
                  {saving ? (t('common.saving') || '…') : (t('common.save') || 'Speichern')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
